import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, PackageOpen, Rocket, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Loading } from "../components/ui";
import { api } from "../lib/api";

interface IndustryPackOption {
  key: string;
  name: string;
  description: string;
  objects: string[];
  pipelines: string[];
}

interface SetupStatus {
  completed: boolean;
  workspaceName: string;
  industry: string;
  packKey: string;
  recordCount: number;
}

export function SetupPage() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [step, setStep] = useState(0);
  const [workspaceName, setWorkspaceName] = useState("");
  const [packKey, setPackKey] = useState("universal");
  const [seedDemo, setSeedDemo] = useState(false);

  const status = useQuery({ queryKey: ["setup-status"], queryFn: () => api<SetupStatus>("/api/v1/setup/status") });
  const packs = useQuery({ queryKey: ["industry-packs"], queryFn: () => api<{ packs: IndustryPackOption[] }>("/api/v1/industry-packs") });

  useEffect(() => {
    if (status.data) {
      setWorkspaceName((current) => current || status.data.workspaceName);
      setPackKey((current) => (current === "universal" ? status.data.industry : current));
    }
  }, [status.data]);

  const finish = useMutation({
    mutationFn: (skipped: boolean) =>
      api("/api/v1/setup/complete", {
        method: "POST",
        body: JSON.stringify({ packKey, seedDemo: skipped ? false : seedDemo, workspaceName: workspaceName.trim() || undefined, skipped }),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["setup-status"] });
      await client.invalidateQueries({ queryKey: ["overview"] });
      navigate("/", { replace: true });
    },
  });

  if (status.isPending || packs.isPending) return <div className="fullscreen"><Loading label="Preparing your workspace" /></div>;

  const packList = packs.data?.packs ?? [];
  const selectedPack = packList.find((pack) => pack.key === packKey);

  return (
    <div className="setup-page">
      <div className="setup-card">
        <div className="setup-head">
          <span className="setup-mark"><Rocket size={20} /></span>
          <div>
            <h1>Set up your workspace</h1>
            <p>Three quick steps. You can change everything later in Settings.</p>
          </div>
          <Badge tone="purple">Step {step + 1} of 3</Badge>
        </div>

        <div className="setup-progress"><i style={{ width: `${((step + 1) / 3) * 100}%` }} /></div>

        {step === 0 && (
          <div className="setup-step">
            <h2>Name your workspace</h2>
            <p>This is the home for your customer data. It stays on your own Cloudflare account.</p>
            <label>Workspace name
              <input value={workspaceName} onChange={(event) => setWorkspaceName(event.target.value)} placeholder="My Company" autoFocus />
            </label>
          </div>
        )}

        {step === 1 && (
          <div className="setup-step">
            <h2>Pick an industry pack</h2>
            <p>Packs install objects, fields and pipelines tuned to a business model. Universal is a neutral CRM foundation.</p>
            <div className="setup-packs">
              {packList.map((pack) => (
                <button type="button" key={pack.key} className={pack.key === packKey ? "setup-pack active" : "setup-pack"} onClick={() => setPackKey(pack.key)}>
                  <span><PackageOpen size={17} /><strong>{pack.name}</strong>{pack.key === packKey && <Check size={16} />}</span>
                  <small>{pack.description}</small>
                  <em>{pack.objects.length} objects · {pack.pipelines.length} pipelines</em>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="setup-step">
            <h2>Start with demo data?</h2>
            <p>Load a safe, synthetic course-sales dataset so you can explore pipelines, segments and journeys immediately. Demo contacts use reserved <code>example.test</code> addresses and external delivery stays disabled.</p>
            <label className="setup-toggle">
              <input type="checkbox" checked={seedDemo} onChange={(event) => setSeedDemo(event.target.checked)} />
              <span><Sparkles size={16} /> Load the course-sales demo after setup</span>
            </label>
            <div className="setup-summary">
              <div><span>Workspace</span><strong>{workspaceName || "My Workspace"}</strong></div>
              <div><span>Industry pack</span><strong>{selectedPack?.name ?? packKey}</strong></div>
              <div><span>Demo data</span><strong>{seedDemo ? "Course-sales demo" : "Start empty"}</strong></div>
            </div>
          </div>
        )}

        {finish.error && <div className="form-error">{finish.error.message}</div>}

        <div className="setup-actions">
          <Button variant="ghost" disabled={finish.isPending} onClick={() => finish.mutate(true)}>Skip for now</Button>
          <span />
          {step > 0 && <Button variant="secondary" onClick={() => setStep(step - 1)}><ArrowLeft size={16} /> Back</Button>}
          {step < 2 && <Button onClick={() => setStep(step + 1)}>Continue <ArrowRight size={16} /></Button>}
          {step === 2 && <Button disabled={finish.isPending} onClick={() => finish.mutate(false)}>{finish.isPending ? "Setting up…" : <>Enter workspace <ArrowRight size={16} /></>}</Button>}
        </div>
      </div>
    </div>
  );
}
